import React from 'react'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import Layout from '../components/Layout'
import { CustomContext, PageProps, QueryString } from '../types'
import Home from '../components/Home'
import profileData from '../server/utils/profile-data'
import AppBanner, { getPropsForAppBanner } from '../components/AppBanner'
import BANNER_TEXT_VALUES from '../components/AppBanner/constants'
import { HOME_PRODUCT_TYPE } from '../constants/products'
import { shouldHideAppBanner } from '../components/AppBanner/utils'
import prePopulateHotelValues from '../server/utils/prepop-helper'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import fetchSignInStatus from '../server/utils/signin-status'
import getCookies from '../server/utils/get-cookies'
import getPclnHeaders from '../server/utils/get-pcln-headers'

const appBannerValues = BANNER_TEXT_VALUES[HOME_PRODUCT_TYPE]

const Homepage: NextPage<PageProps> = ({
  headerHTML,
  footerHTML,
  bootstrapData
}) => {
  const { query } = useRouter()
  const shouldShowAppBanner = !shouldHideAppBanner(
    bootstrapData,
    query as QueryString
  )
  return (
    <FeatureProvider features={bootstrapData.featureToggles}>
      <ContentfulProvider contentful={bootstrapData.contentful}>
        {shouldShowAppBanner ? (
          <AppBanner
            {...{
              ...getPropsForAppBanner(bootstrapData),
              ...appBannerValues
            }}
          />
        ) : null}
        <Layout
          headerHTML={headerHTML}
          footerHTML={footerHTML}
          partner={bootstrapData.partner || null}
        >
          <Home />
        </Layout>
      </ContentfulProvider>
    </FeatureProvider>
  )
}

export async function getServerSideProps(ctx: CustomContext) {
  const { req, res } = ctx
  const pricelineSpecificHeaders = getPclnHeaders(req)
  const dmc = getCookies(req).dmc || ''
  const { webstats: { clientGUID: cguid = '' } = {} } = res.locals

  const bootstrapData = generateBootstrapData(req, res)
  const email = bootstrapData?.customer?.emailAddress
  const signInStatus = await fetchSignInStatus(
    cguid || '',
    dmc,
    pricelineSpecificHeaders
  )

  const [mostRecentBooking, hotelPrePopData, pclnHeaderFooterContent] =
    await Promise.all([
      profileData({
        dmc,
        cguid,
        req,
        email,
        signedIn: signInStatus.signedIn
      }),
      prePopulateHotelValues(ctx.req.query as QueryString),
      fetchPCLNHeaderFooterContent(req, res)
    ])

  const prePopData = hotelPrePopData ? { hotels: hotelPrePopData } : null

  return {
    props: {
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapData,
        signInStatus,
        mostRecentBooking,
        ...(prePopData && { prePopData })
      }
    }
  }
}

export default Homepage
